import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlined from "@mui/icons-material/EditOutlined";
import DeleteOutlined from "@mui/icons-material/DeleteOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import { PHARMACY_HALO_COLORS } from "@/config/colors";

function fmtAuPhone(raw) {
  if (!raw) return raw;
  let digits = String(raw).replace(/\D/g, "");
  // Convert E.164 +61XXXXXXXXX → 0XXXXXXXXX
  if (digits.startsWith("61") && digits.length === 11) digits = "0" + digits.slice(2);
  // #### ### ### (e.g. 0407 195 553)
  if (digits.length === 10) return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  return raw;
}

function PhoneButton({ phone }) {
  if (!phone) return null;
  const digits = String(phone).replace(/\D/g, "");
  return (
    <Tooltip title={`Call ${fmtAuPhone(phone)}`} placement="right">
      <IconButton
        size="small"
        component="a"
        href={`tel:${digits}`}
        aria-label="Call phone number"
        sx={{
          border: `1px solid ${PHARMACY_HALO_COLORS.BLUE}`,
          borderRadius: 1,
          color: PHARMACY_HALO_COLORS.BLUE,
          p: 0.35,
        }}
      >
        <PhoneOutlined sx={{ fontSize: 14 }} />
      </IconButton>
    </Tooltip>
  );
}

const EMPTY_FORM = { contact_name: "", position: "", phone: "", staff_id: "" };

export default function ChemistContactNumbers({ chemistUUID, staffList = [] }) {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // row being edited, or null for new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (!chemistUUID) return;
    try {
      setLoading(true);
      setError(null);
      const res = await phappsRequest(
        API_PATHS.ADMIN_CHEMIST_NUMBERS,
        "GET",
        null,
        { ChemistUUID: chemistUUID }
      );
      setNumbers(res?.numbers ?? []);
    } catch {
      setError("Failed to load other numbers.");
    } finally {
      setLoading(false);
    }
  }, [chemistUUID]);

  useEffect(() => { load(); }, [load]);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setSaveError(null);
    setDialogOpen(true);
  }

  function openEdit(row) {
    setEditing(row);
    setForm({
      contact_name: row.contact_name ?? "",
      position:     row.position ?? "",
      phone:        row.phone ?? "",
      staff_id:     row.staff_id != null ? String(row.staff_id) : "",
    });
    setSaveError(null);
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditing(null);
    setSaveError(null);
  }

  async function handleSave() {
    if (!form.contact_name.trim() || !form.phone.trim()) {
      setSaveError("Name and phone number are required.");
      return;
    }
    try {
      setSaving(true);
      setSaveError(null);
      const payload = {
        contact_name: form.contact_name.trim(),
        position:     form.position.trim() || null,
        phone:        form.phone.trim(),
        staff_id:     form.staff_id ? Number(form.staff_id) : null,
      };
      if (editing) {
        const res = await phappsRequest(
          `${API_PATHS.ADMIN_CHEMIST_NUMBERS}/${editing.id}`,
          "PUT",
          payload
        );
        setNumbers((prev) => prev.map((n) => n.id === editing.id ? res.number : n));
      } else {
        const res = await phappsRequest(
          API_PATHS.ADMIN_CHEMIST_NUMBERS,
          "POST",
          { ChemistUUID: chemistUUID, ...payload }
        );
        setNumbers((prev) => [...prev, res.number]);
      }
      closeDialog();
    } catch {
      setSaveError("Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      setDeleting(true);
      await phappsRequest(`${API_PATHS.ADMIN_CHEMIST_NUMBERS}/${id}`, "DELETE");
      setNumbers((prev) => prev.filter((n) => n.id !== id));
      setDeleteConfirmId(null);
    } catch {
      // silent — row stays in list
    } finally {
      setDeleting(false);
    }
  }

  // Active staff only for dropdown, sorted by name
  const activeStaff = (staffList ?? [])
    .filter((s) => s.IsActive)
    .sort((a, b) => (a.Name ?? "").localeCompare(b.Name ?? ""));

  return (
    <Box sx={{ mt: 0.5 }}>
      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            fontSize: "0.65rem",
          }}
        >
          Other Numbers
        </Typography>
        <Tooltip title="Add number" placement="right">
          <IconButton
            size="small"
            onClick={openAdd}
            sx={{
              border: `1px solid ${PHARMACY_HALO_COLORS.BLUE}`,
              borderRadius: 1,
              color: PHARMACY_HALO_COLORS.BLUE,
              p: 0.2,
            }}
          >
            <AddIcon sx={{ fontSize: 13 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && (
        <CircularProgress size={14} sx={{ color: PHARMACY_HALO_COLORS.BLUE, ml: 0.5 }} />
      )}
      {error && (
        <Typography variant="caption" color="error">{error}</Typography>
      )}

      {!loading && numbers.length === 0 && !error && (
        <Typography variant="caption" sx={{ color: "text.disabled", fontStyle: "italic" }}>
          None added
        </Typography>
      )}

      {/* Number list — capped width/height so it stays compact even with
          many entries; scrolls internally rather than stretching the header. */}
      <Box sx={{ maxWidth: 420, maxHeight: 132, overflowY: "auto", pr: numbers.length > 3 ? 0.5 : 0 }}>
        {numbers.map((row) => (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              mb: 0.5,
              py: 0.4,
              px: 0.75,
              borderRadius: 1,
              backgroundColor: "#f8fafc",
              border: "1px solid #e5e7eb",
              "&:hover .row-actions": { opacity: 1 },
            }}
          >
            <PhoneButton phone={row.phone} />
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              {fmtAuPhone(row.phone)}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.contact_name}
              {row.position ? ` · ${row.position}` : ""}
              {row.staff_name ? ` (${row.staff_name})` : ""}
            </Typography>

            <Box className="row-actions" sx={{ display: "flex", gap: 0.25, opacity: 0, transition: "opacity 0.15s", flexShrink: 0, ml: "auto" }}>
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => openEdit(row)} sx={{ p: 0.3, color: "text.secondary" }}>
                  <EditOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => setDeleteConfirmId(row.id)} sx={{ p: 0.3, color: "#dc2626" }}>
                  <DeleteOutlined sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        ))}
      </Box>

      {/* Add / Edit dialog */}
      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: PHARMACY_HALO_COLORS.BLUE, pb: 1 }}>
          {editing ? "Edit contact number" : "Add contact number"}
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "12px !important" }}>
          {saveError && <Alert severity="warning" sx={{ mb: 0 }}>{saveError}</Alert>}

          <TextField
            label="Name *"
            size="small"
            fullWidth
            value={form.contact_name}
            onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
            inputProps={{ maxLength: 255 }}
          />

          <TextField
            label="Position"
            size="small"
            fullWidth
            value={form.position}
            onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
            inputProps={{ maxLength: 100 }}
            placeholder="e.g. Owner, Pharmacist"
          />

          <TextField
            label="Phone number *"
            size="small"
            fullWidth
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            inputProps={{ maxLength: 50 }}
          />

          <FormControl size="small" fullWidth>
            <InputLabel>Link to staff member (optional)</InputLabel>
            <Select
              label="Link to staff member (optional)"
              value={form.staff_id}
              onChange={(e) => setForm((f) => ({ ...f, staff_id: e.target.value }))}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {activeStaff.map((s) => (
                <MenuItem key={s.StaffID} value={String(s.StaffID)}>
                  {s.Name || s.Login || `Staff #${s.StaffID}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={handleSave}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: PHARMACY_HALO_COLORS.BLUE }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete contact number?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmId(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deleting}
            onClick={() => handleDelete(deleteConfirmId)}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
